import React, { useState, useEffect, useCallback, useRef } from "react"
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "react-bootstrap"
import { API_WEB_URLS } from "constants/constAPI"
import { Fn_GetReport, Fn_FillListData } from "store/Functions"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import DalaliModal from "./DalaliModal"
import QtyDifferenceModal from "./QtyDifferenceModal"
import BrokerLedgerView from "./BrokerLedgerView"
import PartyLedgerView from "./PartyLedgerView"
import { toast } from "react-toastify"

function NewLedgerReport() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Get global dates from Redux store
  const globalDates = useSelector(state => state.GlobalDates)

  // Safe initial dates: ensure toDate is not stuck in past year from localStorage
  const getInitialDates = () => {
    let f = globalDates?.fromDate ? new Date(globalDates.fromDate) : new Date("2025-04-01T00:00:00")
    let t = globalDates?.toDate ? new Date(globalDates.toDate) : new Date()

    if (isNaN(f.getTime())) f = new Date("2025-04-01T00:00:00")
    if (isNaN(t.getTime()) || t < new Date("2026-10-01T00:00:00")) {
      t = new Date()
    }
    return { f, t }
  }

  const initialDates = getInitialDates()
  const [fromDate, setFromDate] = useState(initialDates.f)
  const [toDate, setToDate] = useState(initialDates.t)
  const [selectedItemIds, setSelectedItemIds] = useState([])
  const [activeTab, setActiveTab] = useState("broker") // "broker" | "party"

  const [brokerData, setBrokerData] = useState([])
  const [partyData, setPartyData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const [showDalaliDataModal, setShowDalaliDataModal] = useState(false)
  const [showQtyDiffModal, setShowQtyDiffModal] = useState(false)
  const [selectedLedgerId, setSelectedLedgerId] = useState(null)
  const [selectedFinancialYearId, setSelectedFinancialYearId] = useState(null)
  const [financialYearArray, setFinancialYearArray] = useState([])

  const [showItemModal, setShowItemModal] = useState(false)
  const [tempSelectedItemIds, setTempSelectedItemIds] = useState([])
  const [itemArray, setItemArray] = useState([])

  const loadDataRequestIdRef = useRef(0)

  // Load Items and Financial Years for modals
  useEffect(() => {
    const API_URL_Items = (API_WEB_URLS.MASTER || "Masters") + "/0/token/ItemMaster/Id/0"
    const API_URL_Financial = (API_WEB_URLS.MASTER || "Masters") + "/0/token/FinancialYearMaster/Id/0"

    Fn_FillListData(dispatch, (data) => {
      if (Array.isArray(data)) setItemArray(data)
    }, "gridData", API_URL_Items).catch(() => {})

    Fn_FillListData(dispatch, setFinancialYearArray, "gridData", API_URL_Financial).catch(() => {})
  }, [dispatch])

  // Helper function to format date in local time (YYYY-MM-DD)
  const formatDateLocal = (date) => {
    if (!date) return ""
    if (typeof date === "string") {
      const match = date.match(/^\d{4}-\d{2}-\d{2}/)
      if (match) return match[0]
      const d = new Date(date)
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear()
        const month = String(d.getMonth() + 1).padStart(2, "0")
        const day = String(d.getDate()).padStart(2, "0")
        return `${year}-${month}-${day}`
      }
      return ""
    }
    if (date instanceof Date && !isNaN(date.getTime())) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const day = String(date.getDate()).padStart(2, "0")
      return `${year}-${month}-${day}`
    }
    return ""
  }

  // Get API URL for active tab
  const getApiUrl = (tab = activeTab) => {
    if (tab === "party") {
      return `${API_WEB_URLS.PartyNewLedgerPartyWise || API_WEB_URLS.LedgerDalaliCalculation}/0/token`
    }
    return `${API_WEB_URLS.PartyNewLedgerBrokerWise || API_WEB_URLS.LedgerDalaliCalculationBroker || API_WEB_URLS.LedgerDalaliCalculation}/0/token`
  }

  // Master Data Loader
  const loadData = useCallback(async (
    fDate = fromDate,
    tDate = toDate,
    itemIds = selectedItemIds,
    tab = activeTab
  ) => {
    const fromStr = formatDateLocal(fDate)
    const toStr = formatDateLocal(tDate)

    if (!fromStr || !toStr) return

    const currentReqId = ++loadDataRequestIdRef.current
    setLoading(true)
    setError("")

    try {
      let vformData = new FormData()
      vformData.append("FromDate", fromStr)
      vformData.append("ToDate", toStr)
      const itemIdsStr = Array.isArray(itemIds) ? itemIds.join(",") : (itemIds || "")
      vformData.append("ItemIds", itemIdsStr)

      const targetApiUrl = getApiUrl(tab)

      // Dummy state setter adapter for Fn_GetReport
      const dummySetState = (action) => {
        let rows = []
        if (typeof action === "function") {
          const res = action({ FillArray: [] })
          rows = res?.FillArray || []
        } else if (action?.FillArray) {
          rows = action.FillArray
        } else if (Array.isArray(action)) {
          rows = action
        }

        if (Array.isArray(rows)) {
          if (tab === "party") {
            setPartyData(rows)
          } else {
            setBrokerData(rows)
          }
        }
      }

      const result = await Fn_GetReport(
        dispatch,
        dummySetState,
        "FillArray",
        targetApiUrl,
        { arguList: { id: 0, formData: vformData } },
        true
      )

      if (currentReqId !== loadDataRequestIdRef.current) return

      const finalRows = Array.isArray(result) ? result : (result?.response || [])
      if (tab === "party") {
        setPartyData(finalRows)
      } else {
        setBrokerData(finalRows)
      }
    } catch (err) {
      if (currentReqId === loadDataRequestIdRef.current) {
        console.error("Report fetch error:", err)
        setError("Error loading ledger report. Please retry.")
      }
    } finally {
      if (currentReqId === loadDataRequestIdRef.current) {
        setLoading(false)
      }
    }
  }, [dispatch, fromDate, toDate, selectedItemIds, activeTab])

  // Initial load on component mount
  useEffect(() => {
    loadData(fromDate, toDate, selectedItemIds, activeTab)
  }, [])

  // Auto-reload on date range changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadData(fromDate, toDate, selectedItemIds, activeTab)
    }, 300)
    return () => clearTimeout(timer)
  }, [fromDate, toDate])

  // Auto-reload on item selection change
  useEffect(() => {
    loadData(fromDate, toDate, selectedItemIds, activeTab)
  }, [selectedItemIds])

  // Handle Tab Switch
  const handleTabSwitch = (tab) => {
    if (tab === activeTab) return
    setActiveTab(tab)
    loadData(fromDate, toDate, selectedItemIds, tab)
  }

  // Row double-click navigation
  const handleRowDoubleClick = (row, selectedRowsSet = null) => {
    const currentData = activeTab === "party" ? partyData : brokerData
    const ids = new Set()

    if (selectedRowsSet && selectedRowsSet.size > 0) {
      selectedRowsSet.forEach(id => ids.add(String(id)))
    }

    if (row && row.LedgerId) {
      ids.add(String(row.LedgerId))
    }

    if (ids.size === 0) {
      toast.error("Invalid ledger record.")
      return
    }

    const ledgerIdsList = Array.from(ids)
    const ledgerNamesList = ledgerIdsList.map(id => {
      const match = currentData.find(item => String(item.LedgerId) === String(id))
      return match ? match.LedgerName : `Ledger ${id}`
    })

    navigate("/LedgerReport", {
      state: {
        ledgerIds: ledgerIdsList.join(","),
        ledgerNames: ledgerNamesList.join(", "),
        fromDate: formatDateLocal(fromDate),
        toDate: formatDateLocal(toDate),
        itemIds: Array.isArray(selectedItemIds) ? selectedItemIds.join(",") : (selectedItemIds || ""),
        completed: true,
        notStarted: true,
        pending: true,
        activeTab: activeTab,
      },
    })
  }

  // Party Sauda view action
  const handleViewPartySauda = (row) => {
    if (!row || !row.LedgerId) return
    navigate("/ContractRegister", {
      state: {
        partyId: row.LedgerId,
        partyName: row.LedgerName,
        fromDate: formatDateLocal(fromDate),
        toDate: formatDateLocal(toDate),
      },
    })
  }

  // Open Dalali Modal
  const handleOpenDalali = (ledgerId) => {
    setSelectedLedgerId(ledgerId)
    setShowDalaliDataModal(true)
  }

  // Item Modal Handlers
  const openItemModal = () => {
    setTempSelectedItemIds([...selectedItemIds])
    setShowItemModal(true)
  }

  const closeItemModal = () => {
    setShowItemModal(false)
  }

  const handleItemModalDone = () => {
    setSelectedItemIds([...tempSelectedItemIds])
    setShowItemModal(false)
  }

  return (
    <div className="new-ledger-report-container">
      <style>{`
        .new-ledger-report-container {
          margin: 0;
          padding: 40px 10px 0 10px !important;
          height: 100vh !important;
          max-height: 100vh !important;
          overflow: hidden !important;
          display: flex !important;
          flex-direction: column !important;
          background-color: #f8fafc;
        }

        @media (max-width: 991px) {
          .new-ledger-report-container {
            padding: 2px 4px 0 4px !important;
            height: calc(100dvh - 118px) !important;
            max-height: calc(100dvh - 118px) !important;
          }
        }

        .table-scroll-container {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          overflow-x: auto;
          position: relative;
          -webkit-overflow-scrolling: touch;
        }

        .table-scroll-container table {
          margin-bottom: 0 !important;
          width: 100%;
        }

        .table-scroll-container table thead th {
          font-size: 0.74rem;
          padding: 7px 6px;
          white-space: nowrap !important;
          letter-spacing: 0.2px;
        }

        .table-scroll-container table tbody td {
          font-size: 0.74rem;
          padding: 6px 6px;
          white-space: nowrap !important;
        }

        .totals-row td {
          border-top: 2px solid #ffffff !important;
          padding: 7px 6px !important;
          font-weight: bold;
          white-space: nowrap !important;
        }

        .totals-row td .badge {
          font-size: 0.72rem;
        }

        .form-check-input {
          cursor: pointer;
        }

        .form-check-input:not(:checked) {
          border: 2px solid #475569 !important;
          background-color: #fff !important;
        }

        .form-check-input:checked {
          border: 2px solid #2563eb !important;
          background-color: #2563eb !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e") !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          background-size: contain !important;
        }

        .checkbox-green:checked {
          border-color: #16a34a !important;
          background-color: #16a34a !important;
        }

        .checkbox-red:checked {
          border-color: #dc2626 !important;
          background-color: #dc2626 !important;
        }

        /* Custom Modern Tabs */
        .tab-btn {
          border: none;
          background: transparent;
          font-size: 0.82rem;
          font-weight: 700;
          padding: 7px 16px;
          border-radius: 8px;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          gap: 8px;
          color: #475569;
          cursor: pointer;
        }

        .tab-btn:hover {
          color: #0f172a;
          background-color: #e2e8f0;
        }

        .tab-btn.active-broker {
          background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%);
          color: #ffffff !important;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.28);
        }

        .tab-btn.active-party {
          background: linear-gradient(135deg, #15803d 0%, #16a34a 100%);
          color: #ffffff !important;
          box-shadow: 0 4px 12px rgba(22, 163, 74, 0.28);
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .spin {
          animation: spin 0.8s linear infinite;
        }

        @media (max-width: 768px) {
          .tab-btn {
            font-size: 0.73rem;
            padding: 6px 8px;
            flex: 1;
            justify-content: center;
          }
          .new-ledger-header {
            padding: 3px 4px !important;
          }
        }
      `}</style>

      {/* DUAL PERSPECTIVE RESPONSIVE TAB BAR (LAPTOP & MOBILE OPTIMIZED) */}
      <div
        className="new-ledger-header bg-white border-bottom px-3 py-1 d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ flexShrink: 0, zIndex: 30 }}
      >
        <div className="d-flex align-items-center gap-1 w-100 w-sm-auto justify-content-between justify-content-sm-start">
          <div className="d-flex align-items-center gap-1 p-1 bg-slate-100 rounded-3 w-100 w-sm-auto" style={{ background: "#f1f5f9", borderRadius: "10px" }}>
            {/* Tab 1: Broker Dalali */}
            <button
              type="button"
              className={`tab-btn ${activeTab === "broker" ? "active-broker" : ""}`}
              onClick={() => handleTabSwitch("broker")}
            >
              <span>👔 Broker Dalali</span>
              <span
                className="badge rounded-pill"
                style={{
                  fontSize: "0.65rem",
                  background: activeTab === "broker" ? "rgba(255,255,255,0.25)" : "#cbd5e1",
                  color: activeTab === "broker" ? "#ffffff" : "#334155",
                  padding: "2px 6px",
                }}
              >
                {brokerData.length}
              </span>
            </button>

            {/* Tab 2: Party Trades & Lifting */}
            <button
              type="button"
              className={`tab-btn ${activeTab === "party" ? "active-party" : ""}`}
              onClick={() => handleTabSwitch("party")}
            >
              <span>🏢 Party Trades & Accounts</span>
              <span
                className="badge rounded-pill"
                style={{
                  fontSize: "0.65rem",
                  background: activeTab === "party" ? "rgba(255,255,255,0.25)" : "#cbd5e1",
                  color: activeTab === "party" ? "#ffffff" : "#334155",
                  padding: "2px 6px",
                }}
              >
                {partyData.length}
              </span>
            </button>
          </div>
        </div>

        {/* Tab Description Badge (Laptop Visible) */}
        <div className="d-none d-sm-flex align-items-center gap-2">
          <span
            className="badge shadow-sm"
            style={{
              fontSize: "0.74rem",
              padding: "5px 10px",
              background: activeTab === "broker" ? "#eff6ff" : "#f0fdf4",
              color: activeTab === "broker" ? "#1e40af" : "#15803d",
              border: `1px solid ${activeTab === "broker" ? "#bfdbfe" : "#bbf7d0"}`,
              fontWeight: "600",
            }}
          >
            {activeTab === "broker"
              ? "🎯 Tracking Dalali Payable to Mediating Brokers (SP: LedgerDalaliCalculationBroker)"
              : "📈 Tracking Customer/Supplier Trades, Deal Value & Lifting (SP: LedgerDalaliCalculationParty)"}
          </span>
        </div>
      </div>

      {/* SUBCOMPONENT VIEW SWITCHER */}
      <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
        {activeTab === "broker" ? (
          <BrokerLedgerView
            data={brokerData}
            loading={loading}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            selectedItemIds={selectedItemIds}
            openItemModal={openItemModal}
            onRefresh={() => loadData(fromDate, toDate, selectedItemIds, "broker")}
            onOpenDalali={handleOpenDalali}
            onRowDoubleClick={handleRowDoubleClick}
            setShowQtyDiffModal={setShowQtyDiffModal}
          />
        ) : (
          <PartyLedgerView
            data={partyData}
            loading={loading}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            selectedItemIds={selectedItemIds}
            openItemModal={openItemModal}
            onRefresh={() => loadData(fromDate, toDate, selectedItemIds, "party")}
            onRowDoubleClick={handleRowDoubleClick}
            onViewContracts={handleViewPartySauda}
            setShowQtyDiffModal={setShowQtyDiffModal}
          />
        )}
      </div>

      {/* DalaliModal */}
      <DalaliModal
        showDalaliDataModal={showDalaliDataModal}
        onHideDalaliDataModal={() => setShowDalaliDataModal(false)}
        ledgerId={selectedLedgerId}
        reportData={brokerData}
        fromDate={fromDate}
        toDate={toDate}
        financialYears={financialYearArray}
        selectedFinancialYearId={selectedFinancialYearId}
        onFinancialYearChange={id => setSelectedFinancialYearId(id)}
        onSaveSuccess={() => loadData(fromDate, toDate, selectedItemIds, "broker")}
      />

      {/* QtyDifferenceModal */}
      <QtyDifferenceModal
        isOpen={showQtyDiffModal}
        onClose={() => setShowQtyDiffModal(false)}
        fromDate={fromDate}
        toDate={toDate}
        selectedLedgerIds={new Set()}
        totalSellerQty={0}
        totalBuyerQty={0}
      />

      {/* Multi-Item Selection Modal */}
      <Modal show={showItemModal} onHide={closeItemModal} size="md" centered style={{ zIndex: 10000 }}>
        <ModalHeader className="bg-primary text-white py-2">
          <div className="d-flex justify-content-between align-items-center w-100">
            <h6 className="mb-0 fw-bold">
              📦 Select Commodities / Items
            </h6>
            <Button variant="light" size="sm" onClick={closeItemModal} className="btn-close btn-close-white" style={{ border: "none" }} />
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "1rem", maxHeight: "55vh", overflowY: "auto" }}>
          <div style={{ maxHeight: "350px", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "6px", padding: "8px" }}>
            {itemArray.length > 0 ? (
              itemArray.map((item) => (
                <div
                  key={item.Id}
                  style={{
                    padding: "6px 10px",
                    cursor: "pointer",
                    borderRadius: "4px",
                    backgroundColor: tempSelectedItemIds.includes(item.Id) ? "#e0f2fe" : "transparent",
                    marginBottom: "3px",
                    fontSize: "0.82rem",
                  }}
                  onClick={() => {
                    const isSelected = tempSelectedItemIds.includes(item.Id)
                    if (isSelected) {
                      setTempSelectedItemIds(tempSelectedItemIds.filter(id => id !== item.Id))
                    } else {
                      setTempSelectedItemIds([...tempSelectedItemIds, item.Id])
                    }
                  }}
                >
                  <div className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      checked={tempSelectedItemIds.includes(item.Id)}
                      onChange={() => {}}
                      style={{ marginRight: "10px", cursor: "pointer" }}
                    />
                    <span className="fw-semibold text-dark">{item.Name}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-muted py-3" style={{ fontSize: "0.8rem" }}>
                No items available
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter className="py-2 d-flex justify-content-between">
          <Button variant="outline-secondary" size="sm" onClick={() => setTempSelectedItemIds([])}>
            Clear
          </Button>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                const all = itemArray.map(i => i.Id)
                setTempSelectedItemIds(all)
              }}
            >
              Select All
            </Button>
            <Button variant="primary" size="sm" onClick={handleItemModalDone}>
              Apply Filter
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default NewLedgerReport
